import {
  createMemo,
  createSignal,
  onCleanup,
  onMount,
  type ParentComponent,
} from 'solid-js';
import MarketingNavbar from './MarketingNavbar';
import MarketingFooter from './MarketingFooter';

interface FeatureMarketingLayoutProps {
  pageTag: string;
  title: string;
  subtitle: string;
  lightTheme?: boolean;
  heroRevealOnScroll?: boolean;
}

const FeatureMarketingLayout: ParentComponent<FeatureMarketingLayoutProps> = (
  props
) => {
  const lightTheme = () => props.lightTheme ?? false;
  const heroRevealOnScroll = () => props.heroRevealOnScroll ?? false;
  const [scrollY, setScrollY] = createSignal(0);
  // Cache viewport height in a signal updated only on resize, not on every scroll
  // event. Reading window.innerHeight inside the scroll-driven memo causes a
  // forced reflow because the browser must flush pending style recalculations.
  const [viewportH, setViewportH] = createSignal(
    typeof window !== 'undefined' ? window.innerHeight : 1
  );

  onMount(() => {
    if (!heroRevealOnScroll()) return;
    const onResize = () => setViewportH(window.innerHeight);
    window.addEventListener('resize', onResize, { passive: true });
    onCleanup(() => window.removeEventListener('resize', onResize));

    const onScroll = () => setScrollY(window.scrollY || 0);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    onCleanup(() => window.removeEventListener('scroll', onScroll));
  });

  const heroProgress = createMemo(() => {
    if (!heroRevealOnScroll()) return 0;
    return Math.min(Math.max(scrollY() / Math.max(viewportH(), 1), 0), 1);
  });

  const heroOpacity = createMemo(() =>
    heroRevealOnScroll() ? Math.max(1 - heroProgress() * 1.15, 0) : 1
  );
  const heroTranslateY = createMemo(() =>
    heroRevealOnScroll()
      ? `translateY(${-heroProgress() * 56}px)`
      : 'translateY(0)'
  );

  const contentOpacity = createMemo(() => {
    if (!heroRevealOnScroll()) return 1;
    const p = heroProgress();
    if (p <= 0.1) return 0;
    return Math.min((p - 0.1) / 0.7, 1);
  });

  const contentTranslateY = createMemo(() =>
    heroRevealOnScroll()
      ? `translateY(${(1 - contentOpacity()) * 30}px)`
      : 'translateY(0)'
  );

  return (
    <div
      class={`relative min-h-screen ${lightTheme() ? 'bg-white text-slate-900' : 'bg-gradient-to-b from-amber-500 via-amber-600 to-amber-800 text-white'}`}
    >
      <div
        class={`pointer-events-none fixed inset-0 z-0 ${
          lightTheme()
            ? 'bg-[radial-gradient(circle_at_10%_12%,rgba(251,191,36,0.14),transparent_40%),radial-gradient(circle_at_88%_16%,rgba(120,113,108,0.08),transparent_35%)]'
            : 'bg-[radial-gradient(circle_at_10%_12%,rgba(255,255,255,0.22),transparent_40%),radial-gradient(circle_at_88%_16%,rgba(255,255,255,0.16),transparent_35%)]'
        }`}
      />
      <MarketingNavbar lightTheme={lightTheme()} />

      <main class="relative z-10">
        <section
          class={`mx-auto max-w-7xl px-4 pb-14 pt-16 sm:px-6 lg:px-8 lg:pb-20 lg:pt-20 ${heroRevealOnScroll() ? 'flex min-h-[calc(100vh-4.5rem)] flex-col items-start justify-center' : ''}`}
          style={{
            opacity: `${heroOpacity()}`,
            transform: heroTranslateY(),
          }}
        >
          <p
            class={`text-xs font-semibold uppercase tracking-[0.18em] ${lightTheme() ? 'text-amber-700' : 'text-amber-100/90'}`}
          >
            {props.pageTag}
          </p>
          <h1
            class={`mt-4 max-w-4xl text-4xl font-black leading-[1.05] sm:text-5xl lg:text-6xl ${lightTheme() ? 'text-slate-900' : 'text-white'}`}
          >
            {props.title}
          </h1>
          <p
            class={`mt-6 max-w-3xl text-lg leading-relaxed sm:text-xl ${lightTheme() ? 'text-slate-700' : 'text-amber-50'}`}
          >
            {props.subtitle}
          </p>
        </section>

        <div
          style={{
            opacity: `${contentOpacity()}`,
            transform: contentTranslateY(),
            'pointer-events':
              heroRevealOnScroll() && contentOpacity() < 0.12 ? 'none' : 'auto',
          }}
        >
          {props.children}
        </div>
      </main>

      <MarketingFooter lightTheme={lightTheme()} />
    </div>
  );
};

export default FeatureMarketingLayout;
